import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import {
  AssetManagementService,
  CardType,
} from '../../asset-management-service';
import { DateUtils } from '@app/shared/utils/date.utils';
import { ChartUtilities } from '@app/shared/utils/chartData';
import { VendorStatsCard } from '@app/shared/interfaces/vendor-stats';
import { VendorStatsCardEnum } from '@app/shared/constants/global.enum';
import { ErrorMessages } from '@app/shared/constants/constants';
import { ToastrService } from 'ngx-toastr';

@Component({
  selector: 'app-vendor-stats-card',
  templateUrl: './vendor-stats-card.component.html',
  styleUrl: './vendor-stats-card.component.scss',
})
export class VendorStatsCardComponent implements OnInit {
  chartWidth = 500;
  public chartHeight = 250;
  vendorStatsData: VendorStatsCard[] = [];
  monthOptions: any[] = [];
  selectedMonthValue: string = '';
  monthSelected: any = null;
  yearSelected: any = null;
  palette: string[] = [];
  VendorStats = VendorStatsCardEnum;
  public isLoading = false;
  isDataLoaded: boolean = true;
  imageSrc: string = 'assets/img/no-data-found.png';
  errorMsg: string = ErrorMessages.NoDataAvailable;

  get chartData(): any[] {
    return this.vendorStatsData.map((vendor, index) => ({
      vendor: vendor.vendorName,
      totalCount: vendor.vendorCount,
    }));
  }

  get colorMap(): Record<string, string> {
    const colorMapping: Record<string, string> = {};
    this.vendorStatsData.forEach((vendor, index) => {
      colorMapping[vendor.vendorName.toLowerCase()] = this.palette[index % this.palette.length];
    });
    return colorMapping;
  }

  constructor(
    private readonly router: Router,
    private readonly route: ActivatedRoute,
    private readonly assetManagementService: AssetManagementService,
    private readonly toasterService: ToastrService,
  ) {
    this.palette = ChartUtilities.getChartColors();
    this.monthOptions = DateUtils.generateMonthOptions();
    if (this.monthOptions.length > 0) {
      this.selectedMonthValue = this.monthOptions[0].value;
    }
  }

  trackByVendor(index: number, item: any): string {
    return item.additionalProp || index.toString();
  }

  getPaletteColor(index: number): any {
    if (this.palette && this.palette?.length > 0) {
      return this.palette[index % this.palette?.length];
    }
  }

  handleMonthChange(selectedMonth: any) {
    this.selectedMonthValue = selectedMonth;
    const [month, year] = selectedMonth.split(',').map((s: string) => s.trim());
    this.monthSelected = DateUtils.getMonthName(Number(month) - 1);
    this.yearSelected = year;
    this.getVendorStats(this.monthSelected, this.yearSelected);
  }

  getVendorStats(month: string, year: string) {
    this.isLoading = true;
    this.assetManagementService.getVendorStatsData(month, year).subscribe(
      (data) => {
        this.isLoading = false;
        const vendorStats = data;
        this.vendorStatsData = this.transformToChartFormat(vendorStats.data);
      },
      (error) => {
        this.isLoading = false;
        this.toasterService.error(this.VendorStats.vendorStatsLoadError);
      },
    );
  }

  private transformToChartFormat(apiData: any): any[] {
    if (apiData && typeof apiData === 'object' && !Array.isArray(apiData)) {
      return Object.keys(apiData)
        .map((vendorName) => ({
          vendorName: this.toTitleCase(vendorName.split(' ')[0]),
          vendorCount: apiData[vendorName],
        }))
        .slice(0, 8);
    }

    if (Array.isArray(apiData)) {
      return apiData
        .map((item) => ({
          vendorName: this.toTitleCase(item.vendorName.split(' ')[0]),
          vendorCount: item.vendorCount,
        }))
        .slice(0, 8);
    }
    return [];
  }

  private toTitleCase(str: string): string {
    return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
  }

  ngOnInit() {
    if (this.monthOptions.length > 0) {
      this.selectedMonthValue = this.monthOptions[0].value;
      const [month, year] = this.selectedMonthValue
        .split(',')
        .map((s: string) => s.trim());
      this.monthSelected = DateUtils.getMonthName(Number(month) - 1);
      this.yearSelected = year;
      this.getVendorStats(this.monthSelected, this.yearSelected);
    }
  }

  navigateToVendorStats() {
    this.navigateToMyRequests('vendor-stats');
  }

  navigateToMyRequests(cardType: CardType = 'header'): void {
    // Removed commented code per linting rules
    localStorage.setItem('selectedCard', cardType);
    this.router.navigate(['vendor-stats'], {
      relativeTo: this.route,
      state: { selectedMonth: this.selectedMonthValue },
    });
  }
}
